import React, { useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";

const FAQS = [
  {
    category: "Getting Started",
    items: [
      {
        q: "How do I apply for a wholesale account?",
        a: "Click 'Apply for Wholesale Access' on the homepage or visit /register. Fill out your business details and submit your application. Once your account is approved and your email is verified, you'll have full access to wholesale pricing and ordering.",
      },
      {
        q: "Who is eligible for a wholesale account?",
        a: "Wholesale accounts are available to licensed retailers, convenience stores, smoke shops, and other business entities authorized to purchase and resell our products. We do not sell to individual consumers.",
      },
      {
        q: "How long does account approval take?",
        a: "Most accounts are reviewed and approved quickly. After submitting your application, verify your email address to activate your account. If you have questions about your application status, contact us at (214) 635-6340.",
      },
    ],
  },
  {
    category: "Ordering & Pricing",
    items: [
      {
        q: "How do I view wholesale pricing?",
        a: "Wholesale pricing is only visible to logged-in verified accounts. Once your account is approved and you're signed in, prices will be displayed on all product pages and throughout the catalog.",
      },
      {
        q: "Is there a minimum order requirement?",
        a: "Minimum order requirements may vary depending on your account type and delivery location. Contact us directly to discuss minimums specific to your account.",
      },
      {
        q: "Can I order online?",
        a: "Yes. Once you're logged into your wholesale account, you can browse the full catalog, add items to your cart, and place orders directly through the website.",
      },
      {
        q: "What payment methods do you accept?",
        a: "Please contact us at (214) 635-6340 or khojadistributions@gmail.com for information about accepted payment methods for your account.",
      },
    ],
  },
  {
    category: "Delivery",
    items: [
      {
        q: "Where do you deliver?",
        a: "We deliver across the entire DFW metroplex and to many surrounding cities and towns throughout Texas. This includes Dallas, Fort Worth, Arlington, Garland, Plano, Frisco, Denton, McKinney, and many smaller communities. Contact us if you're unsure whether we cover your area.",
      },
      {
        q: "How long does delivery take?",
        a: "Orders are typically delivered within 1–2 business days of placement. Orders placed before 12:00 PM on a business day are generally scheduled for next-day delivery. Delivery estimates are not guaranteed and may vary by location and order volume.",
      },
      {
        q: "Do I need to be present for delivery?",
        a: "Yes, someone should be available at your business address to receive and sign for the order during business hours. If no one is available, we'll attempt to contact you and may reschedule the delivery.",
      },
      {
        q: "What if my order arrives damaged or incorrect?",
        a: "Inspect your order at the time of delivery. If anything is damaged, missing, or incorrect, note it immediately and contact us within 48 hours with photos of the issue. Claims submitted after 48 hours may not be eligible for resolution.",
      },
    ],
  },
  {
    category: "Products",
    items: [
      {
        q: "What product categories do you carry?",
        a: "We carry disposable vapes, kratom products, delta/hemp-derived products, glass, and novelty items — everything your store needs from a single wholesale supplier.",
      },
      {
        q: "Are your products age-restricted?",
        a: "Yes. Many products we carry, including tobacco, vaping devices, and related items, are restricted to adults 21 years of age or older. It is the retailer's responsibility to comply with all applicable age verification laws at the point of sale.",
      },
      {
        q: "Are your delta/hemp products Farm Bill compliant?",
        a: "Our hemp-derived products are sourced to comply with the 2018 Farm Bill, containing less than 0.3% Delta-9 THC on a dry weight basis. Retailers are responsible for verifying compliance with state and local regulations in their jurisdiction.",
      },
    ],
  },
  {
    category: "Account & Support",
    items: [
      {
        q: "How do I reset my password?",
        a: "Go to the Sign In page and click 'Forgot password?' Enter your registered email address and we'll send you a reset link. The link expires after 1 hour.",
      },
      {
        q: "How do I update my delivery address?",
        a: "Log in to your account, go to Account Settings, and update your shipping address under the Shipping Address section.",
      },
      {
        q: "How do I view my order history?",
        a: "Log in and navigate to the Orders section from your account dashboard. All past orders including status and items are listed there.",
      },
      {
        q: "How do I contact support?",
        a: "You can reach us by phone at (214) 635-6340, by email at khojadistributions@gmail.com, or by submitting a message through our Support page. We're available Monday–Saturday, 10am–6pm.",
      },
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
      >
        <span className={`text-sm font-bold transition-colors ${open ? "text-brand-amber" : "text-brand-navy"}`}>{q}</span>
        <LuChevronDown size={16} className={`flex-shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="text-sm text-slate-500 leading-relaxed pb-4">{a}</p>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-brand-surface font-sans">
      <Helmet>
        <title>FAQ | Khoja Distributions — Wholesale DFW</title>
        <meta name="description" content="Frequently asked questions about Khoja Distributions wholesale accounts, ordering, delivery across DFW and Texas, products, and account support." />
        <link rel="canonical" href="https://www.khojadistributions.com/faq" />
      </Helmet>

      {/* Hero */}
      <div className="bg-brand-navy py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block bg-brand-amber/20 border border-brand-amber/30 text-brand-amber text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-6">
            FAQ
          </span>
          <h1 className="text-4xl font-black text-white tracking-tight mb-4">Frequently Asked Questions</h1>
          <p className="text-slate-400 text-base">Everything you need to know about wholesale accounts, ordering, and delivery.</p>
        </div>
      </div>

      {/* FAQ sections */}
      <div className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        {FAQS.map((section, i) => (
          <ScrollReveal key={section.category} delay={i * 0.07}>
            <div>
              <h2 className="text-xs font-black text-brand-amber uppercase tracking-widest mb-4">{section.category}</h2>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-6">
                {section.items.map((item) => (
                  <FAQItem key={item.q} {...item} />
                ))}
              </div>
            </div>
          </ScrollReveal>
        ))}

        {/* Still have questions */}
        <ScrollReveal delay={0.1}>
          <div className="bg-slate-200 rounded-2xl p-8 text-center">
            <h2 className="text-lg font-black text-slate-900 mb-2">Still have questions?</h2>
            <p className="text-slate-500 text-sm mb-6">Our team is happy to help. Reach out and we'll get back to you quickly.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="tel:2146356340" className="bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold px-6 py-3 rounded-xl text-sm">
                Call (214) 635-6340
              </a>
              <Link to="/support" className="border border-slate-300 bg-white text-brand-navy hover:bg-slate-50 font-bold px-6 py-3 rounded-xl text-sm transition-colors">
                Submit a Request
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
